import { useState } from 'react';
import { useGT } from 'gt-react';
import { toast } from 'sonner';
import { useGenerateCanvasLayout } from '@/api/rpc/hooks/core/useGenerateCanvasLayout';
import { componentSize, type ComponentRegistry } from './registry';
import type { ReportDataset } from './ReportDataContext';
import type { GridLayout } from './types';
import { serializeLayout } from './serialization';
import {
  buildComponentCatalog,
  buildDatasetCatalog,
  componentGenerationPrompt,
  runCanvasGeneration,
  runComponentGeneration,
} from './canvasGeneration';
import type { ReportComponentConfig } from './types';

// ── "Prompt the canvas" generation, decoupled from any one surface ────────────
// Owns the prompt text, the in-flight flag, and a one-level pre-generation
// snapshot (for Revert). The caller owns *where* the layout lives — it passes
// the current layout in and an `onApply` to write the generated/reverted one
// back — so the AR analytics section (an edit draft) and the procurement report
// builder (page state) share the exact same generation behavior.

interface UseCanvasGenerationOptions {
  /** The layout to send as context + snapshot; null when nothing to edit. */
  layout: GridLayout | null;
  registry: ComponentRegistry;
  datasets: ReportDataset[];
  defaultDatasetKey: string;
  /** Writes a generated (or reverted) layout back into the caller's state. */
  onApply: (layout: GridLayout) => void;
}

export interface CanvasGenerationControls {
  prompt: string;
  setPrompt: (value: string) => void;
  isGenerating: boolean;
  /** True when a generation happened and its pre-generation layout is restorable. */
  canRevert: boolean;
  generate: () => Promise<void>;
  revert: () => void;
  /** Clears prompt + snapshot (e.g. when the caller leaves edit mode). */
  reset: () => void;
}

export function useCanvasGeneration({
  layout,
  registry,
  datasets,
  defaultDatasetKey,
  onApply,
}: UseCanvasGenerationOptions): CanvasGenerationControls {
  const gt = useGT();
  const generateLayout = useGenerateCanvasLayout();
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  // The layout as it was before the last generation — powers one-level Revert.
  // State (not a ref) because the Revert affordance renders off it.
  const [preGenerateSnapshot, setPreGenerateSnapshot] =
    useState<GridLayout | null>(null);

  const generate = async () => {
    if (!layout || !prompt.trim() || isGenerating) return;
    const snapshot = layout;
    const request = {
      prompt: prompt.trim(),
      currentLayout: serializeLayout(layout),
      datasets: buildDatasetCatalog(datasets),
      components: buildComponentCatalog(registry),
    };
    setIsGenerating(true);
    try {
      const result = await runCanvasGeneration({
        generate: async (previousAttempt) =>
          (await generateLayout.mutateAsync({ ...request, previousAttempt }))
            .layout,
        registry,
        datasets,
        defaultDatasetKey,
      });
      onApply(result.layout);
      setPreGenerateSnapshot(snapshot);
      setPrompt('');
      if (result.droppedCount > 0) {
        toast.warning(
          gt('Some widgets could not be generated and were left out'),
        );
      }
    } catch {
      toast.error(gt('Could not generate that layout — try rephrasing'));
    } finally {
      setIsGenerating(false);
    }
  };

  const revert = () => {
    if (!preGenerateSnapshot) return;
    onApply(preGenerateSnapshot);
    setPreGenerateSnapshot(null);
  };

  const reset = () => {
    setPrompt('');
    setPreGenerateSnapshot(null);
  };

  return {
    prompt,
    setPrompt,
    isGenerating,
    canRevert: preGenerateSnapshot !== null,
    generate,
    revert,
    reset,
  };
}

export interface ComponentGenerationControls {
  isGenerating: boolean;
  /** The generated component, or null when generation failed (already toasted). */
  generate: (
    component: ReportComponentConfig,
    prompt: string,
  ) => Promise<ReportComponentConfig | null>;
}

/**
 * "Edit with AI" for one component: the canvas generation contract scoped to
 * that component (see `runComponentGeneration`). The caller writes the result
 * back where the component lives.
 */
export function useComponentGeneration({
  registry,
  datasets,
  defaultDatasetKey,
}: Omit<
  UseCanvasGenerationOptions,
  'layout' | 'onApply'
>): ComponentGenerationControls {
  const gt = useGT();
  const generateLayout = useGenerateCanvasLayout();
  const [isGenerating, setIsGenerating] = useState(false);

  const generate = async (component: ReportComponentConfig, prompt: string) => {
    if (!prompt.trim() || isGenerating) return null;
    const request = {
      prompt: componentGenerationPrompt(component, prompt.trim()),
      currentLayout: serializeLayout([
        {
          id: component.id,
          x: 0,
          y: 0,
          ...componentSize(registry, component.type),
          component,
        },
      ]),
      datasets: buildDatasetCatalog(datasets),
      components: buildComponentCatalog(registry),
    };
    setIsGenerating(true);
    let generated: ReportComponentConfig | null = null;
    try {
      generated = await runComponentGeneration({
        component,
        generate: async (previousAttempt) =>
          (await generateLayout.mutateAsync({ ...request, previousAttempt }))
            .layout,
        registry,
        datasets,
        defaultDatasetKey,
      });
    } catch {
      toast.error(
        gt('Could not generate that chart — try rephrasing', {
          context: 'report canvas component generation failed',
        }),
      );
    }
    setIsGenerating(false);
    return generated;
  };

  return { isGenerating, generate };
}
